<!-- Modal Overlay -->
<div *ngIf="isOpen" class="modal-overlay" (click)="onClose()">
  <!-- Modal Content -->
  <div class="modal-content" (click)="$event.stopPropagation()">
    <!-- Modal Header -->
    <div class="modal-header-fixed">
      <div>
        <h2 class="modal-title">User Analytics</h2>
        <p class="modal-subtitle">
          Overview of all registered users and their word usage statistics
        </p>
      </div>
      <button (click)="onClose()" class="close-button">
        <svg
          class="w-6 h-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M6 18L18 6M6 6l12 12"
          ></path>
        </svg>
      </button>
    </div>

    <!-- Modal Body -->
    <div class="modal-body">
      <!-- Loading State -->
      <div *ngIf="isLoading" class="flex justify-center items-center py-12">
        <div
          class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"
        ></div>
      </div>

      <!-- Error State -->
      <div
        *ngIf="error && !isLoading"
        class="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4"
      >
        <p class="font-semibold">Error</p>
        <p>{{ error }}</p>
        <button
          (click)="loadUsers()"
          class="mt-2 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded"
        >
          Retry
        </button>
      </div>

      <!-- Main Content -->
      <div *ngIf="!isLoading && !error">
        <!-- Search and Stats Bar -->
        <div
          class="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-4"
        >
          <div
            class="flex flex-col md:flex-row md:items-center md:justify-between gap-4"
          >
            <!-- Search -->
            <div class="flex-1 max-w-md">
              <input
                type="text"
                [(ngModel)]="searchTerm"
                (ngModelChange)="onSearchChange()"
                placeholder="Search by name or email..."
                class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <!-- Stats -->
            <div class="flex gap-6 text-sm">
              <div class="text-center">
                <p class="text-gray-500">Total Users</p>
                <p class="text-2xl font-bold text-gray-900">
                  {{ filteredUsers.length }}
                </p>
              </div>
            </div>
          </div>
        </div>

        <!-- Users Table -->
        <div
          class="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
        >
          <div class="overflow-x-auto">
            <table class="analytics-table w-full divide-y divide-gray-200">
              <thead class="bg-gray-50">
                <tr>
                  <th
                    scope="col"
                    class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    style="width: 200px"
                    (click)="sortBy('name')"
                  >
                    Name {{ getSortIcon("name") }}
                  </th>
                  <th
                    scope="col"
                    class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    style="width: 250px"
                    (click)="sortBy('email')"
                  >
                    Email {{ getSortIcon("email") }}
                  </th>
                  <th
                    scope="col"
                    class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    style="width: 100px"
                  >
                    Role
                  </th>
                  <th
                    scope="col"
                    class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    style="width: 120px"
                  >
                    Provider
                  </th>
                  <th
                    scope="col"
                    class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    style="width: 200px"
                    (click)="sortBy('wordsUsed')"
                  >
                    Words Used {{ getSortIcon("wordsUsed") }}
                  </th>
                  <th
                    scope="col"
                    class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    style="width: 150px"
                    (click)="sortBy('imagesUsed')"
                  >
                    Images Used {{ getSortIcon("imagesUsed") }}
                  </th>
                  <th
                    scope="col"
                    class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    style="width: 150px"
                    (click)="sortBy('totalPaymentAmount')"
                  >
                    Total Paid {{ getSortIcon("totalPaymentAmount") }}
                  </th>
                  <th
                    scope="col"
                    class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    style="width: 150px"
                  >
                    Usage
                  </th>
                  <th
                    scope="col"
                    class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    style="width: 120px"
                    (click)="sortBy('lastActiveAt')"
                  >
                    Last Active {{ getSortIcon("lastActiveAt") }}
                  </th>
                </tr>
              </thead>
              <tbody class="bg-white divide-y divide-gray-200">
                <tr *ngFor="let user of filteredUsers" class="hover:bg-gray-50">
                  <!-- Name -->
                  <td class="px-4 py-4">
                    <div class="flex items-center">
                      <div>
                        <div class="text-sm font-medium text-gray-900">
                          {{ user.name }}
                        </div>
                        <div class="text-xs text-gray-500">
                          {{ user.wordsUsed.toLocaleString() }} words used
                        </div>
                      </div>
                    </div>
                  </td>

                  <!-- Email -->
                  <td class="px-4 py-4">
                    <div class="text-sm text-gray-900 break-all">
                      {{ user.email }}
                    </div>
                  </td>

                  <!-- Role -->
                  <td class="px-4 py-4">
                    <span
                      [class]="
                        user.role === 'admin'
                          ? 'px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-purple-100 text-purple-800'
                          : 'px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800'
                      "
                    >
                      {{ user.role }}
                    </span>
                  </td>

                  <!-- Identity Provider -->
                  <td class="px-4 py-4">
                    <span
                      [class]="
                        getProviderBadgeClass(
                          user.identityProvider || 'Unknown'
                        )
                      "
                      class="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full"
                    >
                      {{ user.identityProvider || "Unknown" }}
                    </span>
                  </td>

                  <!-- Words Used -->
                  <td class="px-4 py-4 text-sm text-gray-900">
                    <div class="font-medium">
                      {{ user.wordsUsed.toLocaleString() }} /
                      {{ user.wordsLimit.toLocaleString() }}
                    </div>
                    <div class="text-xs text-gray-500">
                      ({{ user.charactersUsed.toLocaleString() }} chars)
                    </div>
                  </td>

                  <!-- Images Used -->
                  <td class="px-4 py-4 text-sm text-gray-900">
                    <div class="font-medium">
                      {{ (user.imagesUsed || 0).toLocaleString() }} /
                      {{ (user.imagesLimit || 0).toLocaleString() }}
                    </div>
                    <div class="text-xs text-gray-500" *ngIf="user.totalImagesProcessed">
                      (Total: {{ user.totalImagesProcessed }})
                    </div>
                  </td>

                  <!-- Total Payment -->
                  <td class="px-4 py-4 text-sm text-gray-900">
                    <div class="font-medium text-green-600">
                      ${{ (user.totalPaymentAmount || 0).toFixed(2) }}
                    </div>
                  </td>

                  <!-- Usage Progress Bar -->
                  <td class="px-4 py-4">
                    <div class="flex items-center">
                      <div class="w-full bg-gray-200 rounded-full h-2 mr-2">
                        <div
                          [class]="getUsageBarColor(user)"
                          class="h-2 rounded-full transition-all duration-300"
                          [style.width.%]="getUsageBarWidth(user)"
                        ></div>
                      </div>
                      <span class="text-sm text-gray-900 min-w-[2rem]"
                        >{{ getWordUsagePercentage(user).toFixed(1) }}%</span
                      >
                    </div>
                  </td>

                  <!-- Last Active -->
                  <td class="px-4 py-4 text-sm text-gray-500">
                    {{ formatDate(user.lastActiveAt) }}
                  </td>
                </tr>

                <!-- Empty State -->
                <tr *ngIf="filteredUsers.length === 0">
                  <td colspan="9" class="px-6 py-12 text-center text-gray-500">
                    <p class="text-lg mb-2">No users found</p>
                    <p class="text-sm">
                      {{
                        searchTerm
                          ? "Try adjusting your search term"
                          : "No registered users yet"
                      }}
                    </p>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>
